import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROOM_HOLDS_KEY, fetchRoomHolds, releaseRoomHold, type RoomHold } from '../api/roomHolds'
import { MY_ASKS_KEY, STUDIO_ASKS_KEY } from '../api/asks'

/**
 * Weekend rooms teachers are holding, for the studio to take back (D-713).
 *
 * `asked`: "Tonya should be able to take a room back". Nothing books over a hold
 * (D-710), so this is the studio's one way to free a held room - for one date,
 * such as a recital Saturday, or for the rest of the season. The teacher's hours
 * stay either way, and she is told.
 *
 * <b>On Requests</b>, because that is where the studio answered the hold in the
 * first place, and a waiting hold is on the same screen above this.
 */
export default function WeekendHoldsPanel() {
  const holds = useQuery({ queryKey: ROOM_HOLDS_KEY, queryFn: fetchRoomHolds })

  // Nothing held is the ordinary case, and a heading over nothing is noise.
  if (holds.isPending || (holds.data?.length ?? 0) === 0) {
    if (!holds.isError) return null
  }

  return (
    <section className="mb-6">
      <h2 className="mb-2 font-display text-lg">Weekend rooms held</h2>

      {holds.isError ? (
        <p role="alert" className="text-[13px] text-crit">
          Could not load the weekend holds. Please try again.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(holds.data ?? []).map((hold) => (
            <HoldRow key={hold.stretchId} hold={hold} />
          ))}
        </ul>
      )}
    </section>
  )
}

function HoldRow({ hold }: { hold: RoomHold }) {
  const queryClient = useQueryClient()
  const [taking, setTaking] = useState(false)
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('')

  const release = useMutation({
    mutationFn: () => releaseRoomHold(hold.stretchId, date || null, reason),
    onSuccess: () => {
      setTaking(false)
      setDate('')
      setReason('')
      for (const key of [ROOM_HOLDS_KEY, MY_ASKS_KEY, STUDIO_ASKS_KEY, ['bookings'], ['calendar']]) {
        queryClient.invalidateQueries({ queryKey: [...key] })
      }
    },
  })

  const field =
    'rounded-lg border border-line-2 bg-surface-2 px-2 py-1.5 text-[13px] outline-none focus:border-accent focus:bg-surface'

  return (
    <li className="rounded-xl border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[13.5px] text-ink wrap-anywhere">
          <strong>{hold.teacher}</strong> &middot; {hold.room} {hold.when}
        </p>
        <span className="text-[12px] font-semibold text-ink-3">
          {hold.state === 'held' ? 'Held' : 'Waiting on an answer'}
        </span>
      </div>

      {!taking ? (
        <button
          type="button"
          onClick={() => setTaking(true)}
          className="mt-2 min-h-[36px] text-[12.5px] font-semibold text-accent underline underline-offset-2"
        >
          Take the room back
        </button>
      ) : (
        <div className="mt-2 flex flex-col gap-2">
          {hold.state === 'waiting' && (
            <p className="text-[12.5px] text-ink-2">
              This one is still waiting on an answer - answer it above to say no for the season. You can keep one date free here.
            </p>
          )}

          <label className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-2">
            One date only
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={field}
              aria-label={`Date to take back from ${hold.teacher}`}
            />
            {hold.state === 'held' && <span className="text-ink-3">Leave empty for the rest of the season.</span>}
          </label>

          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why, for her (optional)"
            aria-label={`Why you are taking the room back from ${hold.teacher}`}
            className={field}
          />

          {release.isError && (
            <p role="alert" className="text-[12.5px] text-crit">
              {(release.error as Error).message}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={release.isPending || (hold.state === 'waiting' && !date)}
              onClick={() => release.mutate()}
              className="min-h-[36px] rounded-lg bg-accent px-3.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
            >
              {release.isPending
                ? 'Taking it back...'
                : date
                  ? 'Take back that date'
                  : 'Take back for the season'}
            </button>
            <button
              type="button"
              onClick={() => setTaking(false)}
              className="min-h-[36px] rounded-lg border border-line-2 px-3.5 text-[12.5px] font-semibold text-ink-2"
            >
              Not now
            </button>
          </div>

          <p className="text-[12px] text-ink-3">Her hours stay as they are, and she is told.</p>
        </div>
      )}
    </li>
  )
}
